/**
 * useFileTabs — open-file tab state for `Phase0Page` (CR §3.4, Track 3 §5.1).
 *
 * Models the IDE metaphor: artifacts are *files you open*, not cards that are always on screen. The
 * project chat is the home surface and is always present, so the active selection is either
 * {@link CHAT_TAB} or an open artifact type.
 *
 * ── THE ONE NON-OBVIOUS RULE: CLOSE FOCUS ──────────────────────────────────────────────────────
 * Closing a tab destroys the element that had keyboard focus. Left alone the browser drops focus to
 * `<body>`, which strands keyboard users mid-task — the gap Track 3 §4.2 recorded against D9's
 * accept/reject path and asked F1 to fix. {@link closeTab} therefore always returns where focus
 * must go, and the caller is obliged to honour it:
 *   • another file tab remains → the tab that slid into the closed tab's slot (or the last one)
 *   • no file tabs remain      → the chat composer, because that is the surface now on screen
 * Returning the target rather than performing the move keeps this hook DOM-free and testable.
 */

import { useCallback, useState } from 'react';

import { ARTIFACT_TYPES, type ArtifactType } from '@/hooks/useArtifacts';

/** Sentinel for the always-present, non-closeable project-chat tab. */
export const CHAT_TAB = '__chat__' as const;

export type FileTabId = typeof CHAT_TAB | ArtifactType;

/** Where focus must move after a close. The caller performs the actual DOM focus. */
export type CloseFocusTarget =
  | { kind: 'tab'; tabId: ArtifactType }
  | { kind: 'composer' };

export interface UseFileTabsResult {
  /** Open artifact tabs, in the order the user opened them. */
  openTabs: ArtifactType[];
  activeTab: FileTabId;
  /** Opens the file if it is not already open, then activates it. Idempotent. */
  openTab: (artifactType: ArtifactType) => void;
  /** Activates an already-open tab (or the chat tab). */
  activateTab: (tabId: FileTabId) => void;
  /** Closes a tab and reports where focus must move. Returns `null` if the tab was not open. */
  closeTab: (artifactType: ArtifactType) => CloseFocusTarget | null;
  isOpen: (artifactType: ArtifactType) => boolean;
}

export function useFileTabs(initialTabs: ArtifactType[] = []): UseFileTabsResult {
  const [openTabs, setOpenTabs] = useState<ArtifactType[]>(initialTabs);
  const [activeTab, setActiveTab] = useState<FileTabId>(
    initialTabs.length > 0 ? initialTabs[0] : CHAT_TAB,
  );

  const openTab = useCallback((artifactType: ArtifactType): void => {
    setOpenTabs((prev) => (prev.includes(artifactType) ? prev : [...prev, artifactType]));
    setActiveTab(artifactType);
  }, []);

  const activateTab = useCallback((tabId: FileTabId): void => {
    setActiveTab(tabId);
  }, []);

  const closeTab = useCallback(
    (artifactType: ArtifactType): CloseFocusTarget | null => {
      const index = openTabs.indexOf(artifactType);
      if (index === -1) return null;

      const remaining = openTabs.filter((t) => t !== artifactType);
      setOpenTabs(remaining);

      if (remaining.length === 0) {
        setActiveTab(CHAT_TAB);
        return { kind: 'composer' };
      }

      // Prefer the tab that slides into the closed tab's slot — the IDE convention, and the one that
      // keeps the user's eye where it already was. Falls back to the new last tab at the end.
      const neighbour = remaining[Math.min(index, remaining.length - 1)];
      if (activeTab === artifactType) setActiveTab(neighbour);
      return { kind: 'tab', tabId: neighbour };
    },
    [openTabs, activeTab],
  );

  const isOpen = useCallback(
    (artifactType: ArtifactType): boolean => openTabs.includes(artifactType),
    [openTabs],
  );

  return { openTabs, activeTab, openTab, activateTab, closeTab, isOpen };
}

/** Stable display order, used when more than one tab is opened at once. */
export function sortByCanonicalOrder(types: ArtifactType[]): ArtifactType[] {
  return [...types].sort((a, b) => ARTIFACT_TYPES.indexOf(a) - ARTIFACT_TYPES.indexOf(b));
}
